import { Canvas } from 'fabric';

import { TextEntity } from '../../fabricClasses/TextEntity';
import { TextEntityType } from '../../types/fabric-extensions';
import { MenuItem } from '../../types/types';
import { findObjectById } from '../findObjects';

/**
 * Изменить тип сущности TextEntity
 * @param params - Параметры для изменения типа
 */
export const changeEntityType = ({
  targetIds,
  canvas,
  canvasId,
  addBackup,
  newEntityType,
}: MenuItem & {
  addBackup: (canvasId: string, canvasState: unknown) => void;
  newEntityType: TextEntityType;
}) => {
  console.debug('🔄 changeEntityType:', { targetIds, newEntityType });

  const idsToAction = Array.isArray(targetIds) ? targetIds : [targetIds];

  idsToAction.forEach((currentId) => {
    const obj = findObjectById(canvas.getObjects(), currentId);

    if (obj && TextEntity.isTextEntity(obj)) {
      obj.changeType(newEntityType);
      console.debug('✅ Entity type changed:', {
        id: currentId,
        newType: newEntityType,
        fontSize: obj.fontSize,
      });
    } else {
      console.warn('⚠️ Object is not a TextEntity:', { id: currentId, type: obj?.type });
    }
  });

  // Сохраняем изменения в бэкап
  addBackup(canvasId, canvas.toJSON());
  canvas.renderAll();
};

/**
 * Конвертировать текстовую сущность в буллит первого уровня
 * @param params - Параметры для конвертации
 */
export const convertToBullet = ({
  targetIds,
  canvas,
  canvasId,
  addBackup,
  bulletLevel = 1,
}: MenuItem & {
  addBackup: (canvasId: string, canvasState: unknown) => void;
  bulletLevel?: number;
}) => {
  console.debug('🔄 convertToBullet:', { targetIds, bulletLevel });

  const idsToAction = Array.isArray(targetIds) ? targetIds : [targetIds];

  idsToAction.forEach((currentId) => {
    const obj = findObjectById(canvas.getObjects(), currentId);

    if (obj && TextEntity.isTextEntity(obj)) {
      // Определяем тип буллита по уровню
      const bulletType: TextEntityType = `bullet-level-${bulletLevel}` as TextEntityType;
      obj.changeType(bulletType);

      console.debug('✅ Entity converted to bullet:', {
        id: currentId,
        bulletType,
        bulletLevel: obj.bulletLevel,
        marginLeft: obj.marginLeft,
      });
    } else {
      console.warn('⚠️ Object is not a TextEntity:', { id: currentId, type: obj?.type });
    }
  });

  // Сохраняем изменения в бэкап
  addBackup(canvasId, canvas.toJSON());
  canvas.renderAll();
};

/**
 * Изменить уровень буллита (только для существующих буллитов)
 * @param params - Параметры для изменения уровня
 */
export const changeBulletLevel = ({
  targetIds,
  canvas,
  canvasId,
  addBackup,
  newLevel,
}: MenuItem & {
  addBackup: (canvasId: string, canvasState: unknown) => void;
  newLevel: number;
}) => {
  console.debug('🔄 changeBulletLevel:', { targetIds, newLevel });

  const idsToAction = Array.isArray(targetIds) ? targetIds : [targetIds];

  idsToAction.forEach((currentId) => {
    const obj = findObjectById(canvas.getObjects(), currentId);

    if (obj && TextEntity.isTextEntity(obj)) {
      obj.changeBulletLevel(newLevel);

      console.debug('✅ Bullet level changed:', {
        id: currentId,
        newLevel,
        entityType: obj.entityType,
        marginLeft: obj.marginLeft,
      });
    } else {
      console.warn('⚠️ Object is not a TextEntity:', { id: currentId, type: obj?.type });
    }
  });

  // Сохраняем изменения в бэкап
  addBackup(canvasId, canvas.toJSON());
  canvas.renderAll();
};

/**
 * Получить тип сущности TextEntity объекта
 * @param canvas - Canvas instance
 * @param objectId - ID объекта
 * @returns Тип сущности или null если объект не найден/не является TextEntity
 */
export const getEntityType = (canvas: Canvas, objectId: string): TextEntityType | null => {
  const obj = findObjectById(canvas.getObjects(), objectId);

  if (obj && TextEntity.isTextEntity(obj)) {
    return obj.entityType;
  }

  return null;
};

/**
 * Проверить является ли объект TextEntity
 * @param canvas - Canvas instance
 * @param objectId - ID объекта
 * @returns true если объект является TextEntity
 */
export const isTextEntity = (canvas: Canvas, objectId: string): boolean => {
  const obj = findObjectById(canvas.getObjects(), objectId);
  return obj ? TextEntity.isTextEntity(obj) : false;
};
